import {
  dynamicContentTypes,
  jsonCopy,
  StaticContentType,
  staticContentTypes,
} from '@bm-js/atlascloud-shared'
import { CText } from '@bm-js/atlascloud-ui'
import { useMemo } from 'react'
import InfoBox from './InfoBox'
import Draggable from './Draggable'
import ContentTypeTabItem from './ContentTypeTabItem'
import { useEditorActions, useEditorData } from '../utils/hooks'
import { v4 as uuid } from 'uuid'
import { useTr } from '../utils/useTr'
import { TrKey } from '../cnst/translations/trKey.cnst'
import { contentTypeTrKeyMap } from '../utils/tr.util'

const ContentTypeTab = () => {
  const { tr } = useTr()
  const { activePage, backendResponse } = useEditorData()
  const { patchActivePage } = useEditorActions()

  const addStaticComponent = (contentType: StaticContentType<unknown>) => {
    if (!activePage) return
    const newContent = { ...jsonCopy(contentType), uuid: uuid() }
    patchActivePage({
      content: [newContent, ...activePage.content],
    })
  }

  const { website } = backendResponse
  const hasStaticComponent = useMemo(() => {
    const staticContentTypeNames = staticContentTypes.map(
      (contentType) => contentType.name
    )

    if (!activePage?.content) return false
    return staticContentTypeNames.includes(activePage?.content?.[0]?.name)
  }, [activePage])

  const filteredDynamicContentTypes = useMemo(() => {
    return dynamicContentTypes.filter((contentType) => {
      if (!contentType.featureKey) return true
      if (contentType.name === 'grid') return false
      return website.features.includes(contentType.featureKey)
    })
  }, [website.features])

  const filteredStaticContentTypes = useMemo(() => {
    if (hasStaticComponent) return []

    return staticContentTypes.filter((contentType) => {
      if (!contentType.featureKey) return true
      return website.features.includes(contentType.featureKey)
    })
  }, [website.features, hasStaticComponent])

  return (
    <div>
      {!!filteredStaticContentTypes.length && (
        <div className='mt-2 mb-6'>
          <div className='flex gap-2 items-center mb-2'>
            <CText className='text-primary-400 text-sm'>
              {tr(TrKey.editorStaticComponents)}
            </CText>
            <InfoBox w={150} text={tr(TrKey.editorStaticComponentsTooltip)} />
          </div>
          {filteredStaticContentTypes.map((contentType) => (
            <button
              className='bg-primary-400 text-white p-3 rounded-md flex items-center flex-col cursor-pointer w-full mb-2'
              onClick={() => addStaticComponent(contentType)}
              key={contentType.name}
            >
              <CText className='font-light text-sm'>
                {tr(TrKey.editorAdd)}
              </CText>
              <CText>{tr(contentTypeTrKeyMap[contentType.name])}</CText>
            </button>
          ))}
        </div>
      )}
      <div className='flex gap-2 items-center mb-2'>
        <CText className='text-primary-400 text-sm'>
          {tr(TrKey.editorDynamicComponents)}
        </CText>
        <InfoBox w={150} text={tr(TrKey.editorDynamicComponentsTooltip)} />
      </div>
      <div className='grid grid-cols-2 gap-2'>
        {filteredDynamicContentTypes.map((contentType) => (
          <Draggable
            data={{
              content: contentType,
              source: 'sidebar',
            }}
            key={contentType.name}
            id={`${JSON.stringify(contentType)}`}
          >
            <ContentTypeTabItem contentType={contentType} />
          </Draggable>
        ))}
      </div>
    </div>
  )
}

export default ContentTypeTab
